<template>
  <span class="cs-notification-watcher" />
</template>

<script>
/**
 * 全局通知守望（迁移自 AICS 原工作台 hooks/useSessionNotification）
 * 挂载于 Layout：无论客服停留在哪个页面，都能收到新进线 / 排队 / 转接请求 / 客户新消息提醒。
 */
import { getMySessions, getQueue } from '@/api/customerService/agent'
import base from '@/api/base'
import { createStompClient } from './useWs'
import {
  notifyWithSound,
  playNotificationSound,
  showBrowserNotification,
  unlockAudio
} from './useNotification'

const POLL_MS = 5000
const MSG_SOUND_THROTTLE_MS = 3000

export default {
  name: 'SessionNotificationWatcher',
  computed: {
    agentId() {
      return this.$store.getters['customerService/agentId']
    }
  },
  watch: {
    agentId: {
      immediate: true,
      handler(v) {
        if (v) this.start(v)
        else this.stop()
      }
    }
  },
  created() {
    this._prevSessionIds = null
    this._prevQueueIds = null
    this._lastSoundAt = 0
    this._runningAgentId = null
    this._ws = null
    this._pollTimer = null
  },
  mounted() {
    unlockAudio()
  },
  beforeDestroy() {
    this.stop()
  },
  methods: {
    start(agentId) {
      if (this._runningAgentId === String(agentId)) return
      this.stop()
      this._runningAgentId = String(agentId)
      this._prevSessionIds = null
      this._prevQueueIds = null
      this.pollAndNotify()
      this._pollTimer = setInterval(() => this.pollAndNotify(), POLL_MS)
      this.connectWs(agentId)
    },
    stop() {
      this._runningAgentId = null
      if (this._pollTimer) {
        clearInterval(this._pollTimer)
        this._pollTimer = null
      }
      if (this._ws) {
        this._ws.disconnect()
        this._ws = null
      }
    },
    async pollAndNotify() {
      const agentId = this.agentId
      if (!agentId) return
      try {
        const results = await Promise.all([getMySessions(agentId), getQueue()])
        const mySessions = (results[0] && results[0].result) || []
        const queueData = (results[1] && results[1].result) || {}

        const curIds = new Set(mySessions.map(s => String(s.id)))
        if (this._prevSessionIds) {
          const newOnes = mySessions.filter(s => !this._prevSessionIds.has(String(s.id)))
          if (newOnes.length > 0) {
            const name = newOnes[0].customerName || '客户'
            notifyWithSound('新客户进线', name + ' 已分配给您')
          }
        }
        this._prevSessionIds = curIds

        const queueSessions = queueData.sessions || []
        const curQueueIds = new Set(queueSessions.map(s => String(s.id)))
        if (this._prevQueueIds) {
          let hasNew = false
          curQueueIds.forEach(id => {
            if (!this._prevQueueIds.has(id)) hasNew = true
          })
          if (hasNew) notifyWithSound('排队等待', '有新客户排队等待服务')
        }
        this._prevQueueIds = curQueueIds
      } catch (e) {
        /* ignore */
      }
    },
    connectWs(agentId) {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      let srvHost = base.url
        ? base.url.replace(/^https?:\/\//, '')
        : (window.location.host + '/service')
      const url = proto + '://' + srvHost + '/service/api/v1/ws/chat?isAjax=1'
      this._ws = createStompClient(url)
      this._ws.onConnect = () => {
        this._ws.subscribe('/topic/agent/' + agentId, frame => this.handleFrame(frame))
      }
      this._ws.connect()
    },
    handleFrame(frame) {
      try {
        let data = frame.data
        if (data == null && frame.body) {
          try { data = JSON.parse(frame.body) } catch (e) { data = {} }
        }
        if (data == null) return

        if (data.wsType === 'TRANSFER_REQUEST') {
          notifyWithSound('转接请求', '客服 ' + (data.fromAgentNickname || '') + ' 请求转接')
          return
        }
        if (data.wsType === 'TRANSFER_RESULT' || data.wsType === 'TYPING_PREVIEW' || data.wsType === 'READ_RECEIPT') {
          return
        }
        // 仅客户消息提醒
        if (Number(data.senderType) !== 0) return
        const now = Date.now()
        if (now - this._lastSoundAt < MSG_SOUND_THROTTLE_MS) return
        this._lastSoundAt = now

        playNotificationSound()
        if (this.shouldPopup()) {
          const name = data.customerName || '客户'
          const text = String(data.content || '').replace(/<[^>]+>/g, '').slice(0, 60)
          showBrowserNotification('新消息 · ' + name, text)
        }
      } catch (e) {
        /* ignore */
      }
    },
    shouldPopup() {
      if (typeof document !== 'undefined' && document.hidden) return true
      const matched = (this.$route && this.$route.matched) || []
      return !matched.some(r => {
        const c = r.components && r.components.default
        const n = c && (c.name || (c.options && c.options.name))
        return n === 'CustomerSessionList'
      })
    }
  }
}
</script>
